import { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

export type PriceTestOfferSlice = {
  offer_id: 'music_990' | 'music_1990' | string;
  label: string;
  paid_count: number;
  delivered_count: number;
  revenue: number;
  ticket_medio: number | null;
  daily?: { dia: string; pedidos: number; receita: number }[];
};

export type PriceTestRevenueSummary = {
  paid_count: number;
  delivered_count: number;
  revenue: number;
  ticket_medio: number | null;
  by_offer: {
    music_990: PriceTestOfferSlice;
    music_1990: PriceTestOfferSlice;
  };
};

const OFFER_ORDER = ['music_990', 'music_1990'] as const;
const OFFER_COLOR: Record<string, string> = {
  music_990: 'var(--gold)',
  music_1990: '#1e6fa8',
};

function formatBRL(v: number | null | undefined): string {
  return Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDayLabel(yyyymmdd: string): string {
  const [, m, d] = String(yyyymmdd || '').split('-');
  return d && m ? `${d}/${m}` : yyyymmdd;
}

function mergeDaily(summary: PriceTestRevenueSummary) {
  const days = new Map<string, { dia: string; receita_990: number; receita_1990: number }>();
  for (const row of summary.by_offer.music_990.daily || []) {
    days.set(row.dia, { dia: row.dia, receita_990: Number(row.receita || 0), receita_1990: 0 });
  }
  for (const row of summary.by_offer.music_1990.daily || []) {
    const current = days.get(row.dia) || { dia: row.dia, receita_990: 0, receita_1990: 0 };
    current.receita_1990 = Number(row.receita || 0);
    days.set(row.dia, current);
  }
  return [...days.values()].sort((a, b) => a.dia.localeCompare(b.dia));
}

export default function PriceTestOfferRevenue({
  data,
  showChart = true,
  note = 'Receita da música em cada workflow. O upsell de vídeo não entra neste recorte.',
}: {
  data: PriceTestRevenueSummary | null | undefined;
  showChart?: boolean;
  note?: string;
}) {
  const daily = useMemo(() => (data ? mergeDaily(data) : []), [data]);
  const share990 = data && data.revenue > 0 ? (data.by_offer.music_990.revenue / data.revenue) * 100 : 0;
  const share1990 = data && data.revenue > 0 ? (data.by_offer.music_1990.revenue / data.revenue) * 100 : 0;

  return (
    <section style={{ marginTop: 8 }}>
      <h2
        style={{
          fontSize: 14,
          fontWeight: 800,
          letterSpacing: '-0.02em',
          color: 'var(--heading-ink)',
          margin: '24px 0 8px',
        }}
      >
        Ofertas WhatsApp · R$ 9,90 e R$ 19,90
      </h2>
      <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.4 }}>
        {note}
      </p>
      {!data ? (
        <div
          className="admin-glass"
          style={{ background: 'var(--surface-white)', borderRadius: 18, padding: 16 }}
        >
          <p style={{ margin: 0, padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            Não foi possível carregar o recorte destas ofertas.
          </p>
        </div>
      ) : (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 12,
            }}
          >
            {OFFER_ORDER.map((offerId) => {
              const offer = data.by_offer[offerId];
              return (
                <div
                  key={offerId}
                  className="admin-glass"
                  style={{
                    background: 'var(--surface-white)',
                    borderRadius: 18,
                    padding: '17px 19px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    minHeight: 92,
                    borderLeft: `4px solid ${OFFER_COLOR[offerId]}`,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: 'var(--text-muted)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                    }}
                  >
                    {offer.label}
                  </div>
                  <div
                    style={{
                      fontSize: 28,
                      fontWeight: 800,
                      letterSpacing: '-0.02em',
                      color: 'var(--kpi-revenue, #2f7a4a)',
                      lineHeight: 1.1,
                    }}
                  >
                    {formatBRL(offer.revenue)}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {offer.paid_count} pagamentos · ticket {formatBRL(offer.ticket_medio)} · {offer.delivered_count} entregues
                  </div>
                </div>
              );
            })}
            <div
              className="admin-glass"
              style={{
                background: 'var(--surface-white)',
                borderRadius: 18,
                padding: '17px 19px',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                minHeight: 92,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: 'var(--text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                }}
              >
                Total das duas ofertas
              </div>
              <div
                style={{
                  fontSize: 28,
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  color: 'var(--heading-ink)',
                  lineHeight: 1.1,
                }}
              >
                {formatBRL(data.revenue)}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                {data.paid_count} pagamentos · ticket {formatBRL(data.ticket_medio)}
              </div>
            </div>
          </div>

          <div
            className="admin-glass"
            style={{
              background: 'var(--surface-white)',
              borderRadius: 18,
              padding: 16,
              marginTop: 12,
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 8 }}>
              Participação na receita
            </div>
            <div
              role="img"
              aria-label={`${data.by_offer.music_990.label} ${share990.toFixed(1)} por cento, ${data.by_offer.music_1990.label} ${share1990.toFixed(1)} por cento`}
              style={{ height: 14, borderRadius: 999, overflow: 'hidden', background: 'var(--badge-bg)', display: 'flex' }}
            >
              <div style={{ width: `${share990}%`, background: OFFER_COLOR.music_990 }} />
              <div style={{ width: `${share1990}%`, background: OFFER_COLOR.music_1990 }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 8, fontSize: 12, color: 'var(--text-strong)', flexWrap: 'wrap' }}>
              <span>
                <span style={{ color: OFFER_COLOR.music_990, fontWeight: 800 }}>●</span> {data.by_offer.music_990.label} · {share990.toFixed(1)}%
              </span>
              <span>
                <span style={{ color: OFFER_COLOR.music_1990, fontWeight: 800 }}>●</span> {data.by_offer.music_1990.label} · {share1990.toFixed(1)}%
              </span>
            </div>
          </div>

          {showChart && (
            <div
              className="admin-glass"
              style={{
                background: 'var(--surface-white)',
                borderRadius: 18,
                padding: 16,
                marginTop: 12,
              }}
            >
              {daily.length === 0 ? (
                <p style={{ margin: 0, padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                  Sem pagamentos destas ofertas no período.
                </p>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={daily} margin={{ top: 8, right: 8, bottom: 8, left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-fade)" />
                    <XAxis dataKey="dia" tickFormatter={formatDayLabel} fontSize={11} stroke="var(--text-muted)" />
                    <YAxis fontSize={11} stroke="var(--text-muted)" />
                    <Tooltip
                      contentStyle={{
                        background: 'var(--surface-white)',
                        border: '1px solid var(--border-soft)',
                        borderRadius: 8,
                        fontSize: 13,
                      }}
                      labelFormatter={(v) => formatDayLabel(String(v))}
                      formatter={(value: number, name: string) => [
                        formatBRL(value),
                        name === 'receita_990' ? 'R$ 9,90' : 'R$ 19,90',
                      ]}
                    />
                    <Bar dataKey="receita_990" stackId="ofertas" fill="var(--gold)" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="receita_1990" stackId="ofertas" fill="#1e6fa8" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
